package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarLayout
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteComorbidity
import com.partejar.app.feature.gestantes.data.GestanteExam

@Composable
internal fun ClinicalContext(detail: GestanteClinicalDetail, wideLayout: Boolean = false) {
    val spacing = PartejarTheme.spacing
    val fontScale = LocalDensity.current.fontScale
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val readableWidth = maxWidth / fontScale
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
            GestanteSectionHeader(
                title = "Contexto clínico",
                icon = PartejarIcons.Navigation.prontuario
            )
            if (readableWidth < PartejarLayout.formMaxWidth) {
                ClinicalInfoCard("Maternidade", detail.maternity ?: "Não informada",
                    PartejarIcons.Navigation.workspace, Modifier.fillMaxWidth())
                ClinicalInfoCard("Pré-natal", detail.prenatalType?.toPrenatalLabel() ?: "Não informado",
                    PartejarIcons.Navigation.gestantes, Modifier.fillMaxWidth())
                ClinicalInfoCard("Responsável", detail.responsible ?: "Não atribuído",
                    PartejarIcons.Navigation.profile, Modifier.fillMaxWidth())
                ClinicalInfoCard("Mora em BH", if (detail.livesInBh) "Sim" else "Não",
                    PartejarIcons.Navigation.home, Modifier.fillMaxWidth())
                ClinicalInfoCard("Endereço", detail.address.display() ?: "Endereço não informado",
                    PartejarIcons.Navigation.home, Modifier.fillMaxWidth())
            } else if (wideLayout && readableWidth >= PartejarLayout.readingMaxWidth) {
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    ClinicalInfoCard(
                        label = "Maternidade",
                        value = detail.maternity ?: "Não informada",
                        icon = PartejarIcons.Navigation.workspace,
                        modifier = Modifier.weight(1f)
                    )
                    ClinicalInfoCard(
                        label = "Pré-natal",
                        value = detail.prenatalType?.toPrenatalLabel() ?: "Não informado",
                        icon = PartejarIcons.Navigation.gestantes,
                        modifier = Modifier.weight(1f)
                    )
                    ClinicalInfoCard(
                        label = "Responsável",
                        value = detail.responsible ?: "Não atribuído",
                        icon = PartejarIcons.Navigation.profile,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    ClinicalInfoCard(
                        label = "Endereço",
                        value = detail.address.display() ?: "Endereço não informado",
                        icon = PartejarIcons.Navigation.home,
                        modifier = Modifier.weight(2f)
                    )
                    ClinicalInfoCard(
                        label = "Mora em BH",
                        value = if (detail.livesInBh) "Sim" else "Não",
                        icon = PartejarIcons.Navigation.home,
                        modifier = Modifier.weight(1f)
                    )
                }
            } else {
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    ClinicalInfoCard(
                        label = "Maternidade",
                        value = detail.maternity ?: "Não informada",
                        icon = PartejarIcons.Navigation.workspace,
                        modifier = Modifier.weight(1f)
                    )
                    ClinicalInfoCard(
                        label = "Pré-natal",
                        value = detail.prenatalType?.toPrenatalLabel() ?: "Não informado",
                        icon = PartejarIcons.Navigation.gestantes,
                        modifier = Modifier.weight(1f)
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    ClinicalInfoCard(
                        label = "Responsável",
                        value = detail.responsible ?: "Não atribuído",
                        icon = PartejarIcons.Navigation.profile,
                        modifier = Modifier.weight(1f)
                    )
                    ClinicalInfoCard(
                        label = "Mora em BH",
                        value = if (detail.livesInBh) "Sim" else "Não",
                        icon = PartejarIcons.Navigation.home,
                        modifier = Modifier.weight(1f)
                    )
                }
                ClinicalInfoCard(
                    label = "Endereço",
                    value = detail.address.display() ?: "Endereço não informado",
                    icon = PartejarIcons.Navigation.home,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun ClinicalInfoCard(
    label: String,
    value: String,
    icon: ImageVector,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Surface(
        modifier = modifier.heightIn(min = 108.dp),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.surfaceContainerLow,
        contentColor = MaterialTheme.colorScheme.onSurface,
        border = BorderStroke(
            dimensions.strokeThin,
            MaterialTheme.colorScheme.tertiary.copy(alpha = 0.26f)
        )
    ) {
        Column(
            modifier = Modifier.padding(spacing.space16),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            ClinicalInfoTextContent(label = label, value = value, icon = icon)
        }
    }
}

@Composable
private fun ClinicalInfoTextContent(
    label: String,
    value: String,
    icon: ImageVector
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(spacing.space40),
                shape = MaterialTheme.shapes.extraLarge,
                color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
                contentColor = MaterialTheme.colorScheme.tertiary
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(icon, contentDescription = null, modifier = Modifier.size(dimensions.iconMedium))
                }
            }
            Text(
                text = label,
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurface
            )
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(dimensions.strokeThin)
                .background(MaterialTheme.colorScheme.outline.copy(alpha = 0.42f))
        )
        Text(
            text = value,
            modifier = Modifier.fillMaxWidth(),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = Int.MAX_VALUE
        )
    }
}

@Composable
internal fun GestanteSectionHeader(
    title: String,
    icon: ImageVector
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(36.dp),
                shape = MaterialTheme.shapes.extraLarge,
                color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
                contentColor = MaterialTheme.colorScheme.tertiary
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(icon, contentDescription = null, modifier = Modifier.size(dimensions.iconSmall))
                }
            }
            Text(title, style = MaterialTheme.typography.titleLarge)
        }
        Box(
            modifier = Modifier
                .size(width = spacing.space40, height = dimensions.strokeStrong)
                .background(MaterialTheme.colorScheme.tertiary)
        )
    }
}

internal data class ClinicalNavigationSummary(
    val badge: String,
    val preview: String
)

internal fun comorbidityClinicalSummary(
    comorbidities: List<GestanteComorbidity>
): ClinicalNavigationSummary {
    val active = comorbidities.filter { it.active }
    return ClinicalNavigationSummary(
        badge = when (active.size) {
            0 -> "Nenhuma"
            1 -> "1 ativa"
            else -> "${active.size} ativas"
        },
        preview = active
            .take(2)
            .joinToString(" • ") { it.description }
            .ifBlank { "Nenhuma comorbidade ativa registrada." }
    )
}

internal fun examClinicalSummary(exams: List<GestanteExam>): ClinicalNavigationSummary {
    val latest = exams.maxByOrNull { it.performedAt }
    val preview = latest?.let { exam ->
        val date = examDate(exam.performedAt)
        "Último exame: ${exam.type.name.toClinicalDisplayName()} • $date"
    } ?: "Nenhum exame registrado."
    return ClinicalNavigationSummary(
        badge = if (exams.size == 1) "1 exame" else "${exams.size} exames",
        preview = preview
    )
}

private fun String.toClinicalDisplayName(): String = replace('_', ' ')
    .lowercase()
    .replaceFirstChar { it.uppercase() }

@Composable
internal fun ClinicalSummaryNavigationCard(
    title: String,
    summary: ClinicalNavigationSummary,
    icon: ImageVector,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    PartejarCard(onClick = onClick, modifier = modifier) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(spacing.space40),
                shape = MaterialTheme.shapes.extraLarge,
                color = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.10f),
                contentColor = MaterialTheme.colorScheme.tertiary
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(icon, contentDescription = null, modifier = Modifier.size(dimensions.iconMedium))
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(title, style = MaterialTheme.typography.titleSmall)
                    PartejarStatusBadge(summary.badge, tone = PartejarStatusBadgeTone.NEUTRAL)
                }
                Text(
                    text = summary.preview,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Icon(
                imageVector = PartejarIcons.Actions.next,
                contentDescription = "Abrir $title",
                modifier = Modifier.size(dimensions.iconMedium),
                tint = MaterialTheme.colorScheme.primary
            )
        }
    }
}

internal fun String.toPrenatalLabel(): String = when (this) {
    "HABITUAL" -> "Habitual"
    "ALTO_RISCO" -> "Alto risco"
    else -> this
}
